import { useCallback, useEffect, useRef, useState } from 'react';

import { AuthRedirectError } from '@/api/useAuthedClient';

interface UseGraphQLQueryOptions<TData> {
  fetcher: (signal: AbortSignal) => Promise<TData>;
  deps: readonly unknown[];
  enabled?: boolean;
  initialLoading?: boolean;
  onSuccess?: (data: TData, ctx: { isRefetch: boolean }) => void;
}

interface UseGraphQLQueryReturn<TData> {
  data: TData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<TData | null>;
}

export function useGraphQLQuery<TData>(
  options: UseGraphQLQueryOptions<TData>,
): UseGraphQLQueryReturn<TData> {
  const { fetcher, deps, enabled = true, initialLoading, onSuccess } = options;

  const [data, setData] = useState<TData | null>(null);
  const [isLoading, setIsLoading] = useState(initialLoading ?? enabled);
  const [error, setError] = useState<Error | null>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const abortControllerRef = useRef<AbortController | null>(null);

  const runFetch = useCallback(async (isRefetch: boolean): Promise<TData | null> => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setError(null);

    let result: TData;
    let isAuthRedirect = false;
    try {
      result = await fetcherRef.current(controller.signal);
    } catch (err) {
      // Auth0 redirect is in flight — leave the hook in its loading state so
      // consumers don't flash an error UI before the browser navigates away.
      if (err instanceof AuthRedirectError) {
        isAuthRedirect = true;
        return null;
      }
      if (err instanceof Error && err.name === 'AbortError') return null;
      if (controller.signal.aborted) return null;
      setError(err instanceof Error ? err : new Error('Unknown error'));
      return null;
    } finally {
      if (!controller.signal.aborted && !isAuthRedirect) {
        setIsLoading(false);
      }
    }
    if (controller.signal.aborted) return null;
    setData(result);
    try {
      onSuccessRef.current?.(result, { isRefetch });
    } catch {
      // onSuccess threw — don't surface as a fetch error.
    }
    return result;
  }, []);

  useEffect(() => {
    if (!enabled) {
      abortControllerRef.current?.abort();
      setData(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    void runFetch(false);
    return () => abortControllerRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, runFetch, ...deps]);

  const refetch = useCallback(() => runFetch(true), [runFetch]);

  return { data, isLoading, error, refetch };
}
